// iter148 — Kategori Sayfası Yeniden Tasarım
// Kategori girişinde: hero + alt kategori chip'leri + sol filtre paneli + ürün grid + sıralama + infinite scroll
import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { api } from "../../lib/api";
import { useSEO } from "../../hooks/useSEO";
import ProductCard from "../../components/public/ProductCard";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import {
  ChevronRight, X, Loader2, SlidersHorizontal, ArrowUpDown, Grid3x3, Package, Filter,
} from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const PAGE_SIZE = 24;

const SORT_OPTIONS = [
  { v: "featured", l: "Öne Çıkanlar" },
  { v: "newest",   l: "En Yeniler" },
  { v: "price_asc",  l: "Fiyat: Düşük → Yüksek" },
  { v: "price_desc", l: "Fiyat: Yüksek → Düşük" },
  { v: "name_asc",   l: "İsim: A → Z" },
];

function mdToHtml(md) {
  if (!md) return "";
  let html = md
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/^### (.+)$/gm, "<h3>$1</h3>")
    .replace(/^## (.+)$/gm, "<h2>$1</h2>")
    .replace(/^# (.+)$/gm, "<h1>$1</h1>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2">$1</a>')
    .replace(/^\* (.+)$/gm, "<li>$1</li>");
  html = html.replace(/(<li>[\s\S]+?<\/li>)(?![\s\S]*<li>)/g, (m) => `<ul>${m}</ul>`);
  html = html.split(/\n{2,}/).map((p) => /^<(h\d|ul|hr)/.test(p.trim()) ? p : `<p>${p}</p>`).join("\n");
  return html;
}

export default function CategorySEOPage() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState(null);       // {category, children, product_count, ...}
  const [notFound, setNotFound] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({ brands: [], attributes: [], price: { min: null, max: null } });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const sentinelRef = useRef(null);

  const brand_id = params.get("marka") || "";
  const attr_name = params.get("ozellik") || "";
  const attr_value = params.get("deger") || "";
  const min_price = params.get("min") || "";
  const max_price = params.get("max") || "";
  const sort = params.get("sirala") || "featured";

  // Kategori meta bilgisini SEO endpoint'inden çek
  useEffect(() => {
    setNotFound(false); setMeta(null);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/seo/category/${slug}`);
        setMeta(data);
      } catch { setNotFound(true); }
    })();
  }, [slug]);

  const category_id = meta?.category?.id;

  const buildQuery = (extra = {}) => {
    const p = new URLSearchParams();
    const set = (k, v) => v && p.set(k, v);
    set("category_id", category_id);
    set("brand_id", brand_id);
    set("attr_name", attr_name); set("attr_value", attr_value);
    set("min_price", min_price); set("max_price", max_price);
    Object.entries(extra).forEach(([k, v]) => v ? p.set(k, v) : p.delete(k));
    return p.toString();
  };

  // Ürün + facet yükle (kategori id belli olduğunda)
  useEffect(() => {
    if (!category_id) return;
    (async () => {
      setLoading(true); setSkip(0);
      try {
        const [pr, f] = await Promise.all([
          api.get(`/shop/products?limit=${PAGE_SIZE}&skip=0&${buildQuery()}`),
          api.get(`/shop/facets?${buildQuery()}`),
        ]);
        setItems(sortItems(pr.data.items || [], sort));
        setTotal(pr.data.total || 0);
        setFacets(f.data || { brands: [], attributes: [], price: { min: null, max: null } });
      } catch { /* ignore */ }
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, [category_id, brand_id, attr_name, attr_value, min_price, max_price]);

  // Client-side sort (backend'de fiyat sort yok, garanti için burada)
  const sortItems = (arr, s) => {
    const list = [...arr];
    switch (s) {
      case "price_asc":  list.sort((a, b) => (a.price || 999999) - (b.price || 999999)); break;
      case "price_desc": list.sort((a, b) => (b.price || 0) - (a.price || 0)); break;
      case "name_asc":   list.sort((a, b) => (a.name || "").localeCompare(b.name || "")); break;
      case "newest":     /* backend zaten created_at desc */ break;
      case "featured":
      default: break;
    }
    return list;
  };

  // Sıralama parametresi değişince sadece client-side yeniden sırala (yeni fetch yok)
  useEffect(() => { setItems((prev) => sortItems(prev, sort)); }, [sort]);

  const loadMore = useCallback(async () => {
    if (loadingMore || loading || items.length >= total) return;
    setLoadingMore(true);
    const nextSkip = skip + PAGE_SIZE;
    try {
      const { data } = await api.get(`/shop/products?limit=${PAGE_SIZE}&skip=${nextSkip}&${buildQuery()}`);
      setItems((prev) => sortItems([...prev, ...(data.items || [])], sort));
      setSkip(nextSkip);
    } catch { /* ignore */ }
    setLoadingMore(false);
    // eslint-disable-next-line
  }, [items.length, total, loading, loadingMore, skip, brand_id, attr_name, attr_value, min_price, max_price, sort]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const obs = new IntersectionObserver((entries) => { if (entries[0].isIntersecting) loadMore(); }, { rootMargin: "400px" });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [loadMore]);

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p, { replace: true });
  };
  const clearFilters = () => { setParams(new URLSearchParams(), { replace: true }); };

  useSEO({
    title: meta?.category?.seo_title,
    description: meta?.category?.seo_description,
    keywords: meta?.category?.seo_keywords,
    canonical: `/kategori/${slug}`,
    image: meta?.category?.image_url || meta?.category?.image,
    breadcrumbs: meta ? [
      { name: "Anasayfa", path: "/" },
      { name: "Ürünler", path: "/urunler" },
      { name: meta.category.name, path: `/kategori/${slug}` },
    ] : null,
  });

  const activeFilters = useMemo(() => {
    const arr = [];
    if (brand_id) { const b = facets.brands.find((x) => x.id === brand_id); if (b) arr.push({ k: "marka", label: b.name }); }
    if (attr_name && attr_value) arr.push({ k: "ozellik", label: `${attr_name}: ${attr_value}`, extra: "deger" });
    if (min_price || max_price) arr.push({ k: "min", label: `${min_price || 0}₺ - ${max_price || "∞"}₺`, extra: "max" });
    return arr;
  }, [facets, brand_id, attr_name, attr_value, min_price, max_price]);

  if (notFound) return (
    <div className="max-w-3xl mx-auto p-16 text-center">
      <div className="text-6xl mb-4">📦</div>
      <div className="text-2xl font-display font-bold">Kategori bulunamadı</div>
      <Link to="/urunler" className="mt-6 inline-block text-primary hover:underline">← Tüm Ürünler</Link>
    </div>
  );
  if (!meta) return <div className="p-16 text-center text-zinc-500"><Loader2 size={22} className="mx-auto animate-spin mb-2"/> Yükleniyor…</div>;

  const c = meta.category;

  // Filter Panel içeriği (mobil ve desktop paylaşım)
  const FilterPanel = () => (
    <div className="space-y-5">
      {activeFilters.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-xs uppercase text-zinc-500">Aktif Filtreler</h3>
            <button onClick={clearFilters} className="text-[10px] text-red-600 hover:underline" data-testid="clear-filters">Temizle</button>
          </div>
          <div className="flex flex-wrap gap-1">
            {activeFilters.map((f, i) => (
              <button key={i} onClick={() => { const p = new URLSearchParams(params); p.delete(f.k); if (f.extra) p.delete(f.extra); setParams(p); }}
                      className="text-[11px] bg-white border border-zinc-200 rounded px-2 py-1 flex items-center gap-1 hover:bg-red-50 hover:border-red-300">
                {f.label} <X size={10}/>
              </button>
            ))}
          </div>
        </div>
      )}

      {facets.brands.length > 0 && (
        <div>
          <h3 className="font-semibold text-xs uppercase text-zinc-500 mb-2 flex items-center gap-1"><Filter size={11}/> Marka</h3>
          <div className="space-y-1 max-h-56 overflow-y-auto">
            {facets.brands.map((b) => (
              <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-zinc-100 rounded px-2 py-1" data-testid={`cat-page-brand-${b.id}`}>
                <input type="checkbox" checked={brand_id === b.id} onChange={(e) => setParam("marka", e.target.checked ? b.id : "")}/>
                <span className="flex-1">{b.name}</span>
                <span className="text-xs text-zinc-400">{b.count}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {facets.price?.min != null && facets.price?.max != null && facets.price.min !== facets.price.max && (
        <div>
          <h3 className="font-semibold text-xs uppercase text-zinc-500 mb-2">Fiyat (₺)</h3>
          <div className="flex gap-2">
            <Input type="number" placeholder={`min ${Math.floor(facets.price.min)}`} value={min_price}
                   onChange={(e) => setParam("min", e.target.value)} data-testid="cat-page-price-min" className="text-xs"/>
            <Input type="number" placeholder={`max ${Math.ceil(facets.price.max)}`} value={max_price}
                   onChange={(e) => setParam("max", e.target.value)} data-testid="cat-page-price-max" className="text-xs"/>
          </div>
        </div>
      )}

      {facets.attributes.map((a) => (
        <div key={a.name}>
          <h3 className="font-semibold text-xs uppercase text-zinc-500 mb-2">{a.name}</h3>
          <div className="space-y-1 max-h-56 overflow-y-auto">
            {a.values.slice(0, 20).map((v) => {
              const active = attr_name === a.name && attr_value === v.value;
              return (
                <label key={v.value} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-zinc-100 rounded px-2 py-1" data-testid={`cat-page-attr-${a.name}-${v.value}`}>
                  <input type="checkbox" checked={active}
                         onChange={(e) => {
                           const p = new URLSearchParams(params);
                           if (e.target.checked) { p.set("ozellik", a.name); p.set("deger", v.value); }
                           else { p.delete("ozellik"); p.delete("deger"); }
                           setParams(p);
                         }}/>
                  <span className="flex-1">{v.value}</span>
                  <span className="text-xs text-zinc-400">{v.count}</span>
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div data-testid={`category-page-${slug}`}>
      {/* Hero — iter148.1: hero_image + hero_subtitle (adminden yönetilebilir) */}
      {c.hero_image ? (
        <div className="relative overflow-hidden text-white border-b" data-testid="cat-hero-custom" style={{ minHeight: 260 }}>
          <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, rgba(0,0,0,0.6), rgba(0,0,0,0.15)), url(${c.hero_image}) center/cover` }}/>
          <div className="relative max-w-7xl mx-auto px-4 py-10 sm:py-14">
            <div className="text-xs uppercase tracking-widest font-bold mb-2 opacity-90 flex items-center gap-1 flex-wrap">
              <Link to="/" className="hover:underline">Anasayfa</Link>
              <ChevronRight size={12}/>
              <Link to="/urunler" className="hover:underline">Ürünler</Link>
              <ChevronRight size={12}/>
              <span>{c.name}</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-display font-bold">{c.name}</h1>
            {(c.hero_subtitle || c.description) && (
              <p className="mt-3 max-w-3xl text-base md:text-lg opacity-95">{c.hero_subtitle || c.description}</p>
            )}
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <span className="text-xs bg-white/20 backdrop-blur-sm border border-white/30 px-3 py-1 rounded-full font-semibold">{total || meta.product_count || 0} ürün</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-gradient-to-br from-primary/10 via-white to-primary/5 border-b">
          <div className="max-w-7xl mx-auto px-4 py-8 sm:py-10">
            <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-2 flex items-center gap-1 flex-wrap">
              <Link to="/" className="hover:underline">Anasayfa</Link>
              <ChevronRight size={12}/>
              <Link to="/urunler" className="hover:underline">Ürünler</Link>
              <ChevronRight size={12}/>
              <span>{c.name}</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-display font-bold text-zinc-900">{c.name}</h1>
            {(c.hero_subtitle || c.description) && <p className="text-zinc-600 mt-2 max-w-3xl text-sm">{c.hero_subtitle || c.description}</p>}
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <span className="text-xs bg-white px-3 py-1 rounded-full border font-semibold">{total || meta.product_count || 0} ürün</span>
            </div>
          </div>
        </div>
      )}

      {/* Alt kategori kartları — mobil grid, kolay seçilebilir */}
      {(meta.children?.length > 0) && (
        <div className="border-b bg-white" data-testid="cat-children-chips">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-3">Alt Kategoriler</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5">
              {meta.children.map((ch) => (
                <Link
                  key={ch.id}
                  to={`/kategori/${ch.slug || ch.id}`}
                  data-testid={`cat-child-${ch.slug || ch.id}`}
                  className="group flex items-center gap-3 px-3 py-3 rounded-2xl border border-zinc-100 bg-white hover:border-primary/40 hover:shadow-md hover:shadow-zinc-900/5 transition-all active:scale-[0.98]"
                >
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-zinc-50 to-zinc-100 flex items-center justify-center overflow-hidden shrink-0">
                    {ch.image
                      ? <img src={ch.image} alt="" className="w-full h-full object-cover"/>
                      : <Package size={16} className="text-zinc-400 group-hover:text-primary transition-colors"/>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-zinc-900 group-hover:text-primary truncate leading-tight">{ch.name}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Desktop filter sidebar */}
        <aside className="hidden lg:block lg:col-span-1">
          <div className="bg-zinc-50 rounded-lg p-4 sticky top-24 space-y-5 max-h-[calc(100vh-6rem)] overflow-y-auto" data-testid="filter-sidebar">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-700 font-semibold">
              <SlidersHorizontal size={13}/> Filtrele
            </div>
            <FilterPanel/>
          </div>
        </aside>

        {/* Product grid */}
        <div className="lg:col-span-3">
          {/* Sort + mobile filter trigger */}
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setMobileFilterOpen(true)}
              className="lg:hidden inline-flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm font-medium"
              data-testid="mobile-filter-open"
            >
              <SlidersHorizontal size={14}/> Filtre
            </button>
            <div className="flex items-center gap-2 ml-auto">
              <ArrowUpDown size={13} className="text-zinc-400"/>
              <select
                value={sort}
                onChange={(e) => setParam("sirala", e.target.value)}
                className="text-sm border border-zinc-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="cat-page-sort"
              >
                {SORT_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="p-16 text-center text-zinc-500"><Loader2 size={22} className="mx-auto animate-spin mb-2"/> Ürünler yükleniyor…</div>
          ) : items.length === 0 ? (
            <div className="p-16 text-center text-zinc-500 bg-zinc-50 rounded-lg">
              <Package size={40} className="mx-auto text-zinc-300 mb-3"/>
              <div className="font-semibold text-zinc-700">Filtreye uyan ürün bulunamadı</div>
              <button onClick={clearFilters} className="mt-3 text-sm text-primary hover:underline">Filtreleri temizle</button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="cat-page-grid">
                {items.map((p) => <ProductCard key={p.id} p={p}/>)}
              </div>
              {items.length < total && (
                <div ref={sentinelRef} className="py-8 text-center text-zinc-400 text-sm" data-testid="cat-page-sentinel">
                  {loadingMore ? <><Loader2 size={18} className="mx-auto animate-spin"/> Yükleniyor…</> : "↓ Aşağı kaydırın"}
                </div>
              )}
              {items.length > 0 && items.length >= total && (
                <div className="py-6 text-center text-xs text-zinc-400">— Tüm ürünler yüklendi ({total}) —</div>
              )}
            </>
          )}

          {/* SEO içerik altta */}
          {c.seo_content_md && (
            <Card className="mt-10 p-8 prose max-w-none">
              <div className="blog-content" dangerouslySetInnerHTML={{ __html: mdToHtml(c.seo_content_md) }}/>
            </Card>
          )}
        </div>
      </div>

      {/* Mobile filter drawer */}
      {mobileFilterOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 lg:hidden" onClick={() => setMobileFilterOpen(false)}>
          <div className="absolute right-0 top-0 h-full w-[85%] max-w-sm bg-white shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b flex items-center justify-between px-4 h-14">
              <div className="font-semibold text-sm flex items-center gap-2"><SlidersHorizontal size={15}/> Filtrele</div>
              <button onClick={() => setMobileFilterOpen(false)} data-testid="mobile-filter-close" className="p-1 hover:bg-zinc-100 rounded"><X size={20}/></button>
            </div>
            <div className="p-4">
              <FilterPanel/>
            </div>
            <div className="sticky bottom-0 bg-white border-t p-3">
              <Button className="w-full" onClick={() => setMobileFilterOpen(false)} data-testid="mobile-filter-apply">
                Uygula ({total} ürün)
              </Button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .blog-content h1 { font-size: 1.75rem; font-weight: 700; margin: 1rem 0 0.5rem; color: #18181b; }
        .blog-content h2 { font-size: 1.5rem; font-weight: 700; margin: 1rem 0 0.5rem; color: #27272a; }
        .blog-content h3 { font-size: 1.25rem; font-weight: 600; margin: 0.75rem 0 0.5rem; color: #3f3f46; }
        .blog-content p { margin: 0.75rem 0; line-height: 1.7; color: #3f3f46; }
        .blog-content ul { padding-left: 1.25rem; margin: 0.75rem 0; list-style: disc; }
        .blog-content li { margin: 0.25rem 0; }
        .blog-content a { color: #8D1B3D; text-decoration: underline; }
        .blog-content strong { color: #18181b; }
      `}</style>
    </div>
  );
}
